import { useEffect, useRef } from "react";
import { Reveal } from "./Reveal";

const ADS = [
    { name: "Coca-Cola", src: "/assets/ads/coca-cola.webp" },
    { name: "Gatorade", src: "/assets/ads/gatorade.webp" },
    { name: "Coors Light", src: "/assets/ads/coors-light.webp" },
    { name: "Sour Strips", src: "/assets/ads/sour-strips.webp" },
    { name: "Fairlife", src: "/assets/ads/fairlife.webp" },
    { name: "Monster Energy", src: "/assets/ads/monster-energy.webp" },
    { name: "Ferrero Rocher", src: "/assets/ads/ferrero-rocher.webp" },
    { name: "Red Bull Pink Edition", src: "/assets/ads/red-bull-pink.webp" },
];

export const AdCarousel = () => {
    const scrollerRef = useRef(null);
    const pausedRef = useRef(false);
    const resumeTimer = useRef(null);

    useEffect(() => {
        let raf;
        let last = performance.now();
        const tick = (now) => {
            const el = scrollerRef.current;
            const dt = now - last;
            last = now;
            if (el && !pausedRef.current) {
                el.scrollLeft += dt * 0.055;
                if (el.scrollLeft >= el.scrollWidth / 2) {
                    el.scrollLeft -= el.scrollWidth / 2;
                }
            }
            raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, []);

    const pause = () => {
        pausedRef.current = true;
        if (resumeTimer.current) clearTimeout(resumeTimer.current);
    };

    const resume = (delay = 0) => {
        if (resumeTimer.current) clearTimeout(resumeTimer.current);
        resumeTimer.current = setTimeout(() => {
            pausedRef.current = false;
        }, delay);
    };

    return (
        <section id="examples" className="relative overflow-hidden border-y border-teal-900/5 bg-white py-20 lg:py-28">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <Reveal className="mb-12 flex flex-col items-start justify-between gap-6 sm:mb-16 md:flex-row md:items-end">
                    <div>
                        <p className="mb-3 text-xs font-semibold uppercase tracking-[0.22em] text-teal-700">
                            Gallery
                        </p>
                        <h2 className="font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
                            See What You Can Create
                        </h2>
                    </div>
                    <p className="max-w-sm text-sm leading-relaxed text-slate-500 sm:text-base">
                        Real screen-ready ads generated from a single barcode scan — priced,
                        branded, and published in seconds.
                    </p>
                </Reveal>
            </div>

            <Reveal delay={1}>
                <div
                    ref={scrollerRef}
                    data-testid="dynamic-ad-carousel"
                    className="no-scrollbar flex gap-4 overflow-x-auto px-4 sm:gap-6 sm:px-6 lg:px-8"
                    style={{ touchAction: "pan-x" }}
                    onMouseEnter={pause}
                    onMouseLeave={() => resume()}
                    onTouchStart={pause}
                    onTouchEnd={() => resume(1400)}
                >
                    {[...ADS, ...ADS].map((ad, i) => (
                        <figure
                            key={`${ad.name}-${i}`}
                            className="group relative h-[340px] w-auto shrink-0 overflow-hidden rounded-3xl shadow-lg shadow-slate-900/10 ring-1 ring-slate-900/5 transition-shadow duration-500 hover:shadow-2xl hover:shadow-slate-900/20 sm:h-[440px] lg:h-[560px]"
                            data-testid={`carousel-ad-${ad.name.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                            <img
                                src={ad.src}
                                alt={`${ad.name} ad generated with Scan2Ad`}
                                draggable={false}
                                loading="lazy"
                                className="h-full w-auto select-none transition-transform duration-700 ease-out group-hover:scale-[1.05]"
                            />
                            <figcaption className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center justify-between bg-gradient-to-t from-slate-950/70 to-transparent px-5 pb-4 pt-14 opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                                <span className="font-display text-sm font-semibold text-white">
                                    {ad.name}
                                </span>
                                <span className="rounded-full bg-white/20 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-white backdrop-blur-sm">
                                    Made with Scan2Ad
                                </span>
                            </figcaption>
                        </figure>
                    ))}
                </div>
            </Reveal>
        </section>
    );
};
